import { QueryClient, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  ALL_DEVICE_SCHEDULE_QUERY_KEY,
  DEVICE_SCHEDULE_API_URL,
  DEVICE_SCHEDULE_BY_ID_QUERY_KEY,
  DEVICE_SCHEDULE_BY_SCREEN_ID_QUERY_KEY
} from '@/constants';
import { apiUrl } from '@/helpers';
import { ErrorResponse, Options } from '@/types';
import {
  CreateEditDeviceSchedulePayload,
  DeviceSchedule,
  DeviceScheduleContentUpdatePayload,
  RepeatDeviceSchedulePayload
} from '@/types/DeviceScheduleTypes';

export const deviceScheduleEndpoints = {
  createDeviceSchedule: (payload: CreateEditDeviceSchedulePayload) =>
    axios.post(`${apiUrl()}${DEVICE_SCHEDULE_API_URL}`, payload),
  updateDeviceSchedule: (payload: CreateEditDeviceSchedulePayload) =>
    axios.put(`${apiUrl()}${DEVICE_SCHEDULE_API_URL}`, payload),
  repeatDeviceSchedule: (payload: RepeatDeviceSchedulePayload) =>
    axios.post(`${apiUrl()}${DEVICE_SCHEDULE_API_URL}/repeat`, payload),
  updateDeviceScheduleContent: (id: number, payload: DeviceScheduleContentUpdatePayload) =>
    axios.put(`${apiUrl()}${DEVICE_SCHEDULE_API_URL}/${id}/content`, payload),
  getDeviceScheduleByScreenId: (screenId: number) =>
    axios.get(`${apiUrl()}${DEVICE_SCHEDULE_API_URL}/screen/${screenId}`),
  getDeviceScheduleById: (id: number) =>
    axios.get<DeviceSchedule>(`${apiUrl()}${DEVICE_SCHEDULE_API_URL}/${id}`),
  deleteDeviceSchedule: (id: number) => axios.delete(`${apiUrl()}${DEVICE_SCHEDULE_API_URL}/${id}`)
};

export const useUpdateDeviceScheduleMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(deviceScheduleEndpoints.updateDeviceSchedule, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_DEVICE_SCHEDULE_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateDeviceScheduleContentMutation = (
  queryClient: QueryClient,
  options: Options
) => {
  return useMutation(
    ({ id, payload }: { id: number; payload: DeviceScheduleContentUpdatePayload }) =>
      deviceScheduleEndpoints.updateDeviceScheduleContent(id, payload),
    {
      onSuccess: () => {
        options.onSuccess();
        queryClient.invalidateQueries([ALL_DEVICE_SCHEDULE_QUERY_KEY]);
        queryClient.invalidateQueries([DEVICE_SCHEDULE_BY_ID_QUERY_KEY]);
      },
      onError: (error) => {
        options.onError(error as ErrorResponse);
      }
    }
  );
};

export const useCreateDeviceScheduleMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(deviceScheduleEndpoints.createDeviceSchedule, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_DEVICE_SCHEDULE_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useRepeatDeviceScheduleMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(deviceScheduleEndpoints.repeatDeviceSchedule, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_DEVICE_SCHEDULE_QUERY_KEY]);
      queryClient.invalidateQueries([DEVICE_SCHEDULE_BY_ID_QUERY_KEY]);
      queryClient.invalidateQueries([DEVICE_SCHEDULE_BY_SCREEN_ID_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useGetDeviceScheduleByScreenIdQuery = (screenId: number, options?: Options) => {
  return useQuery(
    [DEVICE_SCHEDULE_BY_SCREEN_ID_QUERY_KEY, { screenId }],
    () => deviceScheduleEndpoints.getDeviceScheduleByScreenId(screenId),
    {
      enabled: !!screenId,
      retry: false,
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useGetDeviceScheduleByIdQuery = (id: number, options?: Options) => {
  return useQuery(
    [DEVICE_SCHEDULE_BY_ID_QUERY_KEY, { id }],
    () => deviceScheduleEndpoints.getDeviceScheduleById(id),
    {
      enabled: !!id,
      retry: false,
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useDeleteDeviceScheduleMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(deviceScheduleEndpoints.deleteDeviceSchedule, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_DEVICE_SCHEDULE_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
